const CONVERTIBLE_IMAGE_TYPES = new Set(["image/jpeg", "image/png"]);
const WEBP_MIME_TYPE = "image/webp";
const WEBP_QUALITY = 0.9;
const MAX_IMAGE_PIXELS = 40_000_000;

type DrawableImage = CanvasImageSource & {
  width: number;
  height: number;
  close?: () => void;
};

function webpFileName(name: string): string {
  const base = name.replace(/\.[^.]+$/, "").trim() || "imagen";
  return base + ".webp";
}

function canvasToWebp(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob || blob.type !== WEBP_MIME_TYPE) {
          reject(new Error("Este navegador no pudo generar la imagen WebP."));
          return;
        }
        resolve(blob);
      },
      WEBP_MIME_TYPE,
      WEBP_QUALITY,
    );
  });
}

async function decodeWithImageElement(file: File): Promise<DrawableImage> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = "async";
    image.src = objectUrl;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function decodeImage(file: File): Promise<DrawableImage> {
  if (typeof createImageBitmap === "function") {
    return await createImageBitmap(file, {
      imageOrientation: "from-image",
    });
  }
  return decodeWithImageElement(file);
}

export async function convertUploadToWebp(file: File): Promise<File> {
  if (!CONVERTIBLE_IMAGE_TYPES.has(file.type)) return file;

  let image: DrawableImage | null = null;
  try {
    image = await decodeImage(file);
    const width = image.width;
    const height = image.height;
    if (!width || !height || width * height > MAX_IMAGE_PIXELS) {
      throw new Error(
        "La imagen tiene una resolución demasiado grande. Usá una imagen de hasta 40 megapíxeles.",
      );
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) {
      throw new Error("No se pudo preparar la imagen para convertirla a WebP.");
    }
    context.drawImage(image, 0, 0, width, height);
    const blob = await canvasToWebp(canvas);
    return new File([blob], webpFileName(file.name), {
      type: WEBP_MIME_TYPE,
      lastModified: file.lastModified,
    });
  } catch (reason) {
    if (reason instanceof Error && reason.message.includes("megapíxeles")) throw reason;
    throw new Error(
      "No se pudo convertir la imagen a WebP. Probá con otro archivo JPG, PNG o WebP.",
      { cause: reason },
    );
  } finally {
    image?.close?.();
  }
}
